/**
 * Tasks, as the app has it: the team with its state and Stop team, the task count, search and
 * filters, and the six columns with coloured dots. A card's Edit opens the detail sheet.
 */

import { ArrowUp, CircleExclamation, Funnel, ListCheck } from '@gravity-ui/icons';
import { Popover, Sheet } from '@heroui/react';
import { Kanban } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { ArtEmpty, Button, ChoiceSelect, inputClass, StatusMark } from '../../components/ui.tsx';
import { timeAgo } from '../../hooks.ts';
import type { Task, TaskStatus, Team, Workbench } from '../useWorkbench.ts';
import { Avatar } from './Avatar.tsx';
import { RemoteView } from './RemoteView.tsx';
import { useTeamChoice } from './TeamPicker.tsx';

/** A ListBox id for the empty answer (none, any, all): an empty string is not a reliable id. */
const NONE = '__none';

/** The app's column dots, as tones; each column also has its name, so the colour is never the only signal. */
const COLUMNS: Array<{ id: TaskStatus; label: string; dot: string }> = [
  { id: 'backlog', label: 'Backlog', dot: 'var(--muted)' },
  { id: 'todo', label: 'To Do', dot: 'var(--tone-blue)' },
  { id: 'in-progress', label: 'In Progress', dot: 'var(--tone-amber)' },
  { id: 'review', label: 'Review', dot: 'var(--tone-violet)' },
  { id: 'blocked', label: 'Blocked / Human', dot: 'var(--danger)' },
  { id: 'done', label: 'Done', dot: 'var(--success)' },
];

/** Only a priority worth noticing gets a mark: an icon with its word for the screen reader and the pointer. Medium and low say nothing. */
function PriorityMark({ priority }: { priority: string }) {
  if (priority === 'critical')
    return (
      <span className="flex shrink-0 items-center text-danger" title="Critical priority">
        <CircleExclamation className="size-3.5" aria-hidden="true" />
        <span className="sr-only">Critical priority</span>
      </span>
    );
  if (priority === 'high')
    return (
      <span className="flex shrink-0 items-center text-warning" title="High priority">
        <ArrowUp className="size-3.5" aria-hidden="true" />
        <span className="sr-only">High priority</span>
      </span>
    );
  return null;
}

export function TasksView({ w }: { w: Workbench }) {
  const [teamId, setTeamId] = useTeamChoice(w);
  const teams = w.useView<Team[]>('teams.list', undefined, [], 20_000);
  const tasks = w.useView<Task[]>('board.list', { teamId }, [teamId], 15_000);
  const team = teams.data?.find((t) => t.id === teamId);
  const [q, setQ] = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('');
  const [open, setOpen] = useState<Task>();
  const [newTitle, setNewTitle] = useState('');
  const [comment, setComment] = useState('');
  const rows = useMemo(() => tasks.data ?? [], [tasks.data]);
  const assignees = useMemo(() => [...new Set(rows.map((t) => t.assignee).filter(Boolean) as string[])], [rows]);
  const shown = rows.filter((t) => (!q || t.title.toLowerCase().includes(q.toLowerCase()) || t.id.toLowerCase().includes(q.toLowerCase())) && (!assignee || t.assignee === assignee) && (!priority || t.priority === priority));
  const reloadTeam = () => {
    teams.reload();
    w.activity.reload();
  };

  const filters = (assignee ? 1 : 0) + (priority ? 1 : 0);
  const field = 'h-9 min-w-0 rounded-lg border border-field-border bg-field-background px-3 text-sm outline-none focus:border-accent';
  const create = () => void w.act('Create', () => w.call('board.create', { teamId, title: newTitle.trim() }), () => { setNewTitle(''); tasks.reload(); });

  return (
    <RemoteView
      fill
      actions={
        <>
          <ChoiceSelect label="Team" value={teamId} options={{ ...Object.fromEntries((teams.data ?? []).map((t) => [t.id, t.name])), solo: 'Solo' }} onChange={setTeamId} />
          {team && <StatusMark state={team.running ? 'working' : 'idle'} label={team.running ? 'Running' : 'Stopped'} />}
          {team &&
            (team.running ? (
              <Button size="sm" variant="danger-soft" onPress={() => void w.act('Stop team', () => w.call('teams.stop', { teamId }), reloadTeam)}>
                Stop team
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onPress={() => void w.act('Start team', () => w.call('teams.start', { teamId }), reloadTeam)}>
                Start team
              </Button>
            ))}
        </>
      }
    >
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-5 pb-3">
        <input className={`${field} w-64`} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New task title, then Enter" aria-label="New task title" onKeyDown={(e) => e.key === 'Enter' && newTitle.trim() && create()} />
        <input className={`${field} w-56`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or id" aria-label="Search tasks" />
        <Popover>
          <Button size="sm" variant={filters ? 'secondary' : 'ghost'} aria-label="Filter by assignee and priority">
            <Funnel className="size-3.5" />
            {filters ? `Filters · ${filters}` : 'Filter'}
          </Button>
          <Popover.Content placement="bottom start" className="w-72 max-w-[calc(100vw-1rem)]">
            <Popover.Dialog className="flex flex-col gap-3 p-3" aria-label="Filter tasks">
              <label className="grid gap-1 text-xs text-muted">
                Assignee
                <ChoiceSelect label="Assignee" className="w-full" value={assignee || NONE} options={{ [NONE]: 'Any assignee', ...Object.fromEntries(assignees.map((a) => [a, a])) }} onChange={(v) => setAssignee(v === NONE ? '' : v)} />
              </label>
              <label className="grid gap-1 text-xs text-muted">
                Priority
                <ChoiceSelect label="Priority" className="w-full" value={priority || NONE} options={{ [NONE]: 'Any priority', ...Object.fromEntries(['critical', 'high', 'medium', 'low'].map((p) => [p, p])) }} onChange={(v) => setPriority(v === NONE ? '' : v)} />
              </label>
            </Popover.Dialog>
          </Popover.Content>
        </Popover>
        <span className="ms-auto text-xs tabular-nums text-muted">
          {shown.length === rows.length ? `${rows.length} tasks` : `${shown.length} of ${rows.length} tasks`}
        </span>
      </div>
      {rows.length === 0 && !tasks.loading ? (
        <div className="px-5 pb-6">
          <ArtEmpty tone="green" icon={<ListCheck />} title="The board is empty">
            {w.connected ? 'Add a task above, or describe the work in Team Work and the operations manager plans it onto the board.' : 'The workbench is not connected.'}
          </ArtEmpty>
        </div>
      ) : (
        <div className="min-h-0 min-w-0 flex-1 px-5 pb-4">
          {/* The workbench's board: a column per status with its dot, title and count, cards that open the task. */}
          <Kanban size="sm" className="h-full">
            {COLUMNS.map((c) => {
              const col = shown.filter((t) => t.status === c.id);
              return (
                <Kanban.Column key={c.id}>
                  <Kanban.ColumnHeader>
                    <Kanban.ColumnIndicator style={{ background: c.dot }} />
                    <Kanban.ColumnTitle>{c.label}</Kanban.ColumnTitle>
                    <Kanban.ColumnCount>{col.length}</Kanban.ColumnCount>
                  </Kanban.ColumnHeader>
                  <Kanban.ColumnBody className="min-h-0">
                    <Kanban.ScrollShadow className="max-h-full">
                      <Kanban.CardList aria-label={`${c.label} tasks`} items={col} renderEmptyState={() => <p className="m-0 px-2 py-6 text-center text-xs text-muted">Nothing here</p>}>
                        {(t: Task) => (
                          <Kanban.Card id={t.id} textValue={`${t.id} ${t.title}`} onAction={() => setOpen(t)}>
                            <div className="flex w-full min-w-0 flex-col gap-1.5">
                              <div className="flex items-center gap-1.5 text-xs">
                                <span className="text-muted">{t.id}</span>
                                <PriorityMark priority={t.priority} />
                              </div>
                              <p className="m-0 text-sm font-medium leading-snug text-foreground">{t.title}</p>
                              {(t.labels.length > 0 || t.assignee) && (
                                <div className="flex min-w-0 items-center gap-2 text-xs text-muted">
                                  <span className="min-w-0 flex-1 truncate">{t.labels.slice(0, 3).join(' · ')}</span>
                                  {t.assignee && (
                                    <span className="flex shrink-0 items-center gap-1">
                                      <Avatar name={t.assignee} size="sm" />
                                      {t.assignee}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </Kanban.Card>
                        )}
                      </Kanban.CardList>
                    </Kanban.ScrollShadow>
                  </Kanban.ColumnBody>
                </Kanban.Column>
              );
            })}
          </Kanban>
        </div>
      )}
      <Sheet isOpen={!!open} placement="right" onOpenChange={(o) => !o && setOpen(undefined)}>
        <Sheet.Backdrop>
          <Sheet.Content className="w-full max-w-md">
            <Sheet.Dialog aria-label={open ? `${open.id} ${open.title}` : 'Task'}>
              {open && (
                <>
                  <Sheet.Header>
                    <div className="min-w-0">
                      <div className="mono text-xs text-muted">{open.id}</div>
                      <Sheet.Heading>{open.title}</Sheet.Heading>
                      <div className="mt-1 text-xs text-muted">Updated {timeAgo(open.updatedAt)}</div>
                    </div>
                  </Sheet.Header>
                  <Sheet.Body className="flex flex-col gap-4 overflow-y-auto">
                    <label className="grid gap-1 text-xs text-muted">
                      Status
                      <ChoiceSelect
                        label="Status"
                        className="w-full"
                        value={open.status}
                        options={Object.fromEntries(COLUMNS.map((c) => [c.id, c.label])) as Record<TaskStatus, string>}
                        onChange={(status) => void w.act('Move', () => w.call('board.move', { teamId, taskId: open.id, status }), () => { setOpen({ ...open, status }); tasks.reload(); })}
                      />
                    </label>
                    <label className="grid gap-1 text-xs text-muted">
                      Assignee
                      <ChoiceSelect
                        label="Assignee"
                        className="w-full"
                        value={open.assignee ?? NONE}
                        options={{ [NONE]: 'Unassigned', ...Object.fromEntries([...new Set([...(team?.members ?? []), ...assignees])].map((m) => [m, m])) }}
                        onChange={(v) => {
                          const assignee = v === NONE ? undefined : v;
                          void w.act('Assign', () => w.call('board.assign', { teamId, taskId: open.id, assignee }), () => { setOpen({ ...open, assignee }); tasks.reload(); });
                        }}
                      />
                    </label>
                    <label className="grid gap-1 text-xs text-muted">
                      Comment
                      <textarea className={`${inputClass} min-h-24`} value={comment} onChange={(e) => setComment(e.target.value)} />
                    </label>
                    <div>
                      <Button size="sm" isDisabled={!comment.trim()} onPress={() => void w.act('Comment', () => w.call('board.comment', { teamId, taskId: open.id, body: comment.trim() }), () => setComment(''))}>
                        Post comment
                      </Button>
                    </div>
                    <p className="text-xs text-muted">Description, history, execute and merge are in the app.</p>
                  </Sheet.Body>
                  <Sheet.CloseTrigger />
                </>
              )}
            </Sheet.Dialog>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>
    </RemoteView>
  );
}
